import React, { type FC, type MouseEventHandler } from 'react'
import { twMerge } from 'tailwind-merge'
import cx from 'classnames'
import { Link as RouterLink } from 'react-router'

export interface NavigationItemProps {
  className?: string
  id?: string
  testId?: string
  ariaLabel?: string
  children?: React.ReactNode
  label: string
  href?: string
  onClick?: MouseEventHandler<HTMLDivElement> | MouseEventHandler<HTMLAnchorElement>
  isActive?: boolean
  showArrow?: boolean
  /**
   * Navigation item size
   */
  size?: 'default' | 'small'
  isDisabled?: boolean
}

export const NAVIGATION_ITEM_BASE_CLASS =
  'items-center py-sm px-lg flex gap-2xs w-full transition-colors duration-200 cursor-pointer group'

export const NavigationItem: FC<NavigationItemProps> = ({
  className,
  id,
  testId,
  ariaLabel,
  children,
  label,
  href,
  onClick: onClickProp,
  isActive = false,
  showArrow = false,
  size = 'default',
  isDisabled = false,
  ...props
}) => {
  const baseClass = cx(NAVIGATION_ITEM_BASE_CLASS, {
    'hover:bg-taupe-200 active:bg-taupe-100': !isActive && !isDisabled,
    'bg-taupe-200': isActive,
    'cursor-not-allowed opacity-50': isDisabled,
  })

  const finalClasses = twMerge(baseClass, className)
  const onClick = isDisabled ? undefined : onClickProp

  const content = (
    <>
      <div className="flex items-center gap-3">
        {children}
        <p
          className={cx('text-text-header', {
            'typo-primary-sm-light': !isActive && size === 'small',
            'group-hover:font-normal': !isActive && size === 'small' && !isDisabled,
            'typo-primary-sm-regular': isActive && size === 'small',
            'typo-primary-base-light group-hover:font-normal': !isActive && size === 'default',
            'typo-primary-base-regular': isActive && size === 'default',
          })}
        >
          {label}
        </p>
      </div>
      {showArrow ? (
        <span className="text-text-header font-light" aria-hidden="true">
          →
        </span>
      ) : null}
    </>
  )

  if (href) {
    return isDisabled ? (
      <div className={finalClasses} id={id} data-testid={testId} aria-label={ariaLabel} {...props}>
        {content}
      </div>
    ) : (
      <RouterLink
        to={href}
        className={finalClasses}
        id={id}
        data-testid={testId}
        aria-label={ariaLabel}
        onClick={onClick as MouseEventHandler<HTMLAnchorElement>}
        {...props}
      >
        {content}
      </RouterLink>
    )
  }

  return (
    <div
      className={finalClasses}
      id={id}
      data-testid={testId}
      aria-label={ariaLabel}
      onClick={onClick as MouseEventHandler<HTMLDivElement>}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick(e as any)
              }
            }
          : undefined
      }
      {...props}
    >
      {content}
    </div>
  )
}
